import React, { useState, useEffect, useCallback } from 'react';
import '../styles/FileTree.css';

interface FileNode {
  name: string;
  kind: 'file' | 'directory';
  handle: any; // FileSystemFileHandle | FileSystemDirectoryHandle
  children: FileNode[] | null; // null = directory not loaded yet
  expanded: boolean;
  path: string;
}

interface Props {
  dirHandle: any; // FileSystemDirectoryHandle
  onFileSelect: (path: string, content: string, name: string) => void;
  selectedPath?: string;
  rootName: string;
}

const FILE_ICONS: Record<string, string> = {
  ts: '🔷', tsx: '🔷', js: '🟡', jsx: '🟡', css: '🎨',
  html: '🌐', json: '📋', md: '📝', py: '🐍', go: '🔵',
  sql: '🗄️', sh: '⚙️', env: '🔑', yml: '⚙️', yaml: '⚙️',
  txt: '📄', rs: '🦀', rb: '💎', java: '☕', c: '🔵', cpp: '🔵',
  svg: '🖼️', png: '🖼️', jpg: '🖼️', jpeg: '🖼️', gif: '🖼️',
  toml: '⚙️', gitignore: '👁️', lock: '🔒',
};

const TEXT_EXTS = new Set([
  'ts','tsx','js','jsx','css','scss','html','htm','json','md','txt',
  'py','go','sql','sh','bash','rb','rs','java','c','cpp','h','hpp',
  'yml','yaml','toml','env','config','gitignore','svg','vue','svelte',
  'graphql','gql','ini','log','conf','xml','csv','r','kt','swift','php',
]);

const SKIP_NAMES = new Set(['node_modules', '.git', '__pycache__', 'dist', 'build', '.next', '.cache', 'coverage']);

function getFileIcon(name: string, kind: 'file' | 'directory', expanded?: boolean): string {
  if (kind === 'directory') return expanded ? '📂' : '📁';
  if (name === '.gitignore') return '👁️';
  const ext = name.split('.').pop()?.toLowerCase() || '';
  return FILE_ICONS[ext] || '📄';
}

async function loadDirChildren(dirHandle: any, parentPath: string): Promise<FileNode[]> {
  const nodes: FileNode[] = [];
  try {
    for await (const [name, handle] of dirHandle.entries()) {
      if (SKIP_NAMES.has(name)) continue;
      nodes.push({
        name,
        kind: handle.kind,
        handle,
        children: handle.kind === 'directory' ? null : undefined as any,
        expanded: false,
        path: parentPath ? `${parentPath}/${name}` : name,
      });
    }
  } catch {}
  return nodes.sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === 'directory' ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}

function updateNodeInTree(nodes: FileNode[], path: string, fn: (n: FileNode) => FileNode): FileNode[] {
  return nodes.map(n => {
    if (n.path === path) return fn(n);
    if (n.children) return { ...n, children: updateNodeInTree(n.children, path, fn) };
    return n;
  });
}

function findNodeInTree(nodes: FileNode[], path: string): FileNode | null {
  for (const n of nodes) {
    if (n.path === path) return n;
    if (n.children) {
      const found = findNodeInTree(n.children, path);
      if (found) return found;
    }
  }
  return null;
}

export default function FileTree({ dirHandle, onFileSelect, selectedPath, rootName }: Props) {
  const [nodes, setNodes] = useState<FileNode[]>([]);
  const [loadingPath, setLoadingPath] = useState<string | null>(null);
  const [rootLoading, setRootLoading] = useState(true);

  useEffect(() => {
    setRootLoading(true);
    loadDirChildren(dirHandle, '').then(children => {
      setNodes(children);
      setRootLoading(false);
    });
  }, [dirHandle]);

  const handleClick = useCallback(async (node: FileNode) => {
    if (node.kind === 'directory') {
      if (node.children === null) {
        setLoadingPath(node.path);
        const children = await loadDirChildren(node.handle, node.path);
        setNodes(prev => updateNodeInTree(prev, node.path, n => ({ ...n, children, expanded: true })));
        setLoadingPath(null);
      } else {
        setNodes(prev => updateNodeInTree(prev, node.path, n => ({ ...n, expanded: !n.expanded })));
      }
    } else {
      try {
        const file = await node.handle.getFile();
        const ext = node.name.split('.').pop()?.toLowerCase() || '';
        if (TEXT_EXTS.has(ext) || file.type.startsWith('text/') || (file.size < 500_000 && !file.type.startsWith('image/'))) {
          const content = await file.text();
          onFileSelect(node.path, content, node.name);
        } else {
          onFileSelect(node.path, `[Binary file: ${file.name} — ${Math.round(file.size / 1024)} KB]`, node.name);
        }
      } catch {
        onFileSelect(node.path, '[Could not read file]', node.name);
      }
    }
  }, [onFileSelect]);

  function renderNodes(nodeList: FileNode[], depth: number): React.ReactNode {
    return nodeList.map(node => (
      <div key={node.path} className="ft-node">
        <div
          className={`ft-row${selectedPath === node.path ? ' ft-selected' : ''}`}
          style={{ paddingLeft: 6 + depth * 14 }}
          onClick={() => handleClick(node)}
          title={node.path}
        >
          <span className="ft-icon">{getFileIcon(node.name, node.kind, node.expanded)}</span>
          <span className="ft-name">{node.name}</span>
          {loadingPath === node.path && <span className="ft-spin">⟳</span>}
        </div>
        {node.kind === 'directory' && node.expanded && node.children && node.children.length > 0 && (
          <div className="ft-children">{renderNodes(node.children, depth + 1)}</div>
        )}
        {node.kind === 'directory' && node.expanded && node.children && node.children.length === 0 && (
          <div className="ft-empty-dir" style={{ paddingLeft: 6 + (depth + 1) * 14 }}>empty</div>
        )}
      </div>
    ));
  }

  if (rootLoading) {
    return <div className="ft-root-loading">Loading...</div>;
  }

  return (
    <div className="file-tree">
      <div className="ft-header">
        <span className="ft-header-icon">📁</span>
        <span className="ft-header-name" title={rootName}>{rootName}</span>
      </div>
      <div className="ft-scroll">
        {nodes.length === 0
          ? <div className="ft-root-loading">Empty folder</div>
          : renderNodes(nodes, 0)
        }
      </div>
    </div>
  );
}
